import { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Stack, Typography } from '../../ui/core';
import { useToast } from '../../hooks';
import api from '../../lib/api';
import RemoveExclusionDialog from './RemoveExclusionDialog';
import { formatDateTime, shortId, reasonLabel } from './exclusionFormatters';

const ActiveExclusionsTable = ({ rows, loading, hasMore, loadingMore, onLoadMore, onRemoved }) => {
  const toast = useToast();
  const [rowToRemove, setRowToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const onConfirmRemove = async (removedNote) => {
    setRemoving(true);
    try {
      await api.riderDriverExclusions.removeExclusion({ id: rowToRemove.id, removedNote });
      setRowToRemove(null);
      toast.success('Exclusion removed.');
      onRemoved && (await onRemoved());
    } catch (error) {
      setRowToRemove(null);
      toast.error(error.message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box>
      {!loading && rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
          No active exclusions.
        </Typography>
      ) : (
        <TableContainer>
          {/* minWidth keeps the six columns from compressing (the two 24-char
              ids don't wrap); the container scrolls horizontally on a narrow
              viewport rather than clipping the Remove action. */}
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell>Rider</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.riderMongoId}</TableCell>
                  <TableCell>{row.driverMongoId}</TableCell>
                  <TableCell>{reasonLabel(row.reason)}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography noWrap title={row.note || ''} sx={{ fontSize: '13px' }}>
                      {row.note || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing="2px">
                      <Typography sx={{ fontSize: '13px' }} title={row.createdBy || ''}>
                        {row.createdBy ? shortId(row.createdBy) : 'Unknown admin'}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {formatDateTime(row.createdAt)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" color="error" onClick={() => setRowToRemove(row)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '12px' }}>
          <Button size="small" variant="outlined" disabled={loadingMore} onClick={onLoadMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}

      <RemoveExclusionDialog
        open={!!rowToRemove}
        submitting={removing}
        onConfirm={onConfirmRemove}
        onCancel={() => setRowToRemove(null)}
      />
    </Box>
  );
};

export default ActiveExclusionsTable;
